import type { CellColorId, Difficulty, TemplatePattern } from '../types';
import { countCells } from '../lib/pattern';
import { genericMidiPalette } from './palette';

type DrawApi = {
  set: (x: number, y: number, color: CellColorId) => void;
  rect: (x: number, y: number, w: number, h: number, color: CellColorId) => void;
  ellipse: (cx: number, cy: number, rx: number, ry: number, color: CellColorId) => void;
  line: (x0: number, y0: number, x1: number, y1: number, color: CellColorId, thickness?: number) => void;
  polygon: (points: [number, number][], color: CellColorId) => void;
};

const width = 29;
const height = 29;
const transparent = 'transparent';

function pointInPolygon(x: number, y: number, points: [number, number][]) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const xi = points[i][0];
    const yi = points[i][1];
    const xj = points[j][0];
    const yj = points[j][1];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function makeApi(cells: CellColorId[]): DrawApi {
  const set = (x: number, y: number, color: CellColorId) => {
    const px = Math.round(x);
    const py = Math.round(y);
    if (px < 0 || px >= width || py < 0 || py >= height) return;
    cells[py * width + px] = color;
  };

  return {
    set,
    rect: (x, y, w, h, color) => {
      for (let yy = y; yy < y + h; yy += 1) {
        for (let xx = x; xx < x + w; xx += 1) set(xx, yy, color);
      }
    },
    ellipse: (cx, cy, rx, ry, color) => {
      for (let yy = Math.floor(cy - ry); yy <= Math.ceil(cy + ry); yy += 1) {
        for (let xx = Math.floor(cx - rx); xx <= Math.ceil(cx + rx); xx += 1) {
          const dx = (xx - cx) / rx;
          const dy = (yy - cy) / ry;
          if (dx * dx + dy * dy <= 1) set(xx, yy, color);
        }
      }
    },
    line: (x0, y0, x1, y1, color, thickness = 1) => {
      const dx = Math.abs(x1 - x0);
      const dy = Math.abs(y1 - y0);
      const steps = Math.max(dx, dy, 1);
      for (let step = 0; step <= steps; step += 1) {
        const t = step / steps;
        const x = Math.round(x0 + (x1 - x0) * t);
        const y = Math.round(y0 + (y1 - y0) * t);
        for (let oy = -Math.floor(thickness / 2); oy <= Math.floor(thickness / 2); oy += 1) {
          for (let ox = -Math.floor(thickness / 2); ox <= Math.floor(thickness / 2); ox += 1) set(x + ox, y + oy, color);
        }
      }
    },
    polygon: (points, color) => {
      const xs = points.map(([x]) => x);
      const ys = points.map(([, y]) => y);
      for (let yy = Math.floor(Math.min(...ys)); yy <= Math.ceil(Math.max(...ys)); yy += 1) {
        for (let xx = Math.floor(Math.min(...xs)); xx <= Math.ceil(Math.max(...xs)); xx += 1) {
          if (pointInPolygon(xx + 0.5, yy + 0.5, points)) set(xx, yy, color);
        }
      }
    }
  };
}

function createTemplate(
  id: string,
  title: string,
  category: TemplatePattern['category'],
  difficulty: Difficulty,
  tags: string[],
  draw: (api: DrawApi) => void
): TemplatePattern {
  const cells = Array.from<CellColorId>({ length: width * height }).fill(transparent);
  draw(makeApi(cells));
  return {
    id,
    title,
    category,
    tags,
    width,
    height,
    paletteId: genericMidiPalette.id,
    cells,
    counts: countCells(cells),
    difficulty
  };
}

function heart(api: DrawApi, color: CellColorId, highlight?: CellColorId) {
  for (let y = 4; y < 25; y += 1) {
    for (let x = 4; x < 25; x += 1) {
      const nx = (x - 14) / 9;
      const ny = (y - 15) / 9;
      const value = (nx * nx + ny * ny - 1) ** 3 - nx * nx * ny * ny * ny;
      if (value <= 0) api.set(x, y, color);
    }
  }
  if (highlight) api.rect(10, 8, 3, 2, highlight);
}

function star(api: DrawApi, color: CellColorId) {
  api.polygon(
    [
      [14, 3],
      [17, 11],
      [25, 11],
      [18, 16],
      [21, 25],
      [14, 19],
      [7, 25],
      [10, 16],
      [3, 11],
      [11, 11]
    ],
    color
  );
}

export const templates: TemplatePattern[] = [
  createTemplate('cat-face', 'Orange Cat', 'animals', 'medium', ['pet', 'face'], (api) => {
    api.polygon(
      [
        [7, 10],
        [9, 3],
        [13, 9],
        [16, 9],
        [20, 3],
        [22, 10],
        [23, 22],
        [14, 26],
        [5, 22]
      ],
      'orange'
    );
    api.ellipse(14, 16, 8, 9, 'orange');
    api.ellipse(11, 17, 2, 2, 'white');
    api.ellipse(18, 17, 2, 2, 'white');
    api.set(11, 17, 'black');
    api.set(18, 17, 'black');
    api.rect(13, 20, 3, 2, 'cream');
    api.set(14, 19, 'pink');
    api.line(8, 13, 11, 14, 'brown');
    api.line(21, 13, 18, 14, 'brown');
  }),
  createTemplate('strawberry', 'Strawberry', 'keychains', 'easy', ['fruit', 'cute'], (api) => {
    heart(api, 'red', 'coral');
    api.polygon(
      [
        [9, 7],
        [14, 3],
        [19, 7],
        [16, 9],
        [12, 9]
      ],
      'green'
    );
    [10, 13, 17, 20].forEach((x, index) => api.set(x, 12 + index * 2, 'yellow'));
    api.set(14, 19, 'yellow');
  }),
  createTemplate('tiny-whale', 'Tiny Whale', 'animals', 'easy', ['ocean'], (api) => {
    api.ellipse(13, 16, 9, 6, 'blue');
    api.polygon(
      [
        [21, 15],
        [26, 10],
        [25, 16],
        [27, 21],
        [21, 18]
      ],
      'blue'
    );
    api.ellipse(10, 17, 4, 2, 'sky');
    api.set(9, 14, 'black');
    api.line(8, 21, 18, 21, 'navy');
    api.line(14, 7, 14, 3, 'teal');
    api.line(12, 5, 10, 4, 'teal');
    api.line(16, 5, 18, 4, 'teal');
  }),
  createTemplate('green-dino', 'Green Dino', 'characters', 'medium', ['dinosaur'], (api) => {
    api.ellipse(13, 18, 8, 5, 'green');
    api.ellipse(20, 12, 4, 4, 'green');
    api.polygon(
      [
        [6, 17],
        [2, 14],
        [4, 20]
      ],
      'dark_green'
    );
    api.line(17, 15, 19, 13, 'green', 3);
    api.set(21, 11, 'black');
    api.rect(10, 22, 2, 3, 'dark_green');
    api.rect(16, 22, 2, 3, 'dark_green');
    [11, 14, 17].forEach((x) => api.polygon([[x, 11], [x + 2, 8], [x + 4, 12]], 'yellow'));
  }),
  createTemplate('daisy', 'Daisy', 'flowers', 'easy', ['flower'], (api) => {
    [
      [14, 7],
      [21, 14],
      [14, 21],
      [7, 14],
      [19, 9],
      [19, 19],
      [9, 19],
      [9, 9]
    ].forEach(([x, y]) => api.ellipse(x, y, 4, 3, 'white'));
    api.ellipse(14, 14, 4, 4, 'yellow');
    api.line(14, 18, 14, 26, 'green', 2);
    api.ellipse(10, 23, 4, 2, 'mint');
  }),
  createTemplate('gold-star', 'Gold Star', 'icons', 'easy', ['badge'], (api) => {
    star(api, 'yellow');
    api.line(13, 7, 16, 14, 'gold', 2);
    api.line(7, 12, 13, 16, 'gold', 2);
  }),
  createTemplate('coral-heart', 'Coral Heart', 'coasters', 'easy', ['love'], (api) => {
    heart(api, 'coral', 'pink');
    api.line(8, 16, 14, 22, 'red');
    api.line(20, 16, 14, 22, 'red');
  }),
  createTemplate('rainbow-arc', 'Rainbow Arc', 'coasters', 'easy', ['weather'], (api) => {
    const bands = [
      ['red', 11],
      ['orange', 9],
      ['yellow', 7],
      ['green', 5],
      ['blue', 3]
    ] as const;
    for (let y = 7; y < 26; y += 1) {
      for (let x = 3; x < 26; x += 1) {
        const distance = Math.hypot(x - 14, y - 25);
        const band = bands.find(([, radius]) => distance <= radius && distance > radius - 2);
        if (band) api.set(x, y, band[0]);
      }
    }
    api.ellipse(7, 24, 4, 2, 'white');
    api.ellipse(21, 24, 4, 2, 'white');
  }),
  createTemplate('penguin', 'Penguin', 'animals', 'medium', ['winter'], (api) => {
    api.ellipse(14, 15, 8, 10, 'black');
    api.ellipse(14, 17, 5, 7, 'white');
    api.ellipse(11, 11, 2, 2, 'white');
    api.ellipse(17, 11, 2, 2, 'white');
    api.set(11, 11, 'black');
    api.set(17, 11, 'black');
    api.polygon([[13, 14], [15, 14], [14, 16]], 'orange');
    api.rect(10, 25, 4, 2, 'orange');
    api.rect(16, 25, 4, 2, 'orange');
  }),
  createTemplate('robot', 'Robot Pal', 'characters', 'medium', ['robot', 'retro'], (api) => {
    api.rect(8, 9, 13, 13, 'light_gray');
    api.rect(10, 5, 9, 4, 'gray');
    api.line(14, 5, 14, 2, 'gray');
    api.set(14, 1, 'red');
    api.rect(10, 13, 3, 3, 'sky');
    api.rect(16, 13, 3, 3, 'sky');
    api.line(11, 19, 18, 19, 'charcoal');
    api.line(6, 12, 4, 18, 'gray', 2);
    api.line(22, 12, 25, 18, 'gray', 2);
  }),
  createTemplate('mushroom', 'Mushroom', 'keychains', 'easy', ['forest'], (api) => {
    api.ellipse(14, 11, 10, 6, 'red');
    api.rect(10, 12, 9, 12, 'tan');
    api.ellipse(10, 10, 2, 2, 'white');
    api.ellipse(15, 8, 2, 2, 'white');
    api.ellipse(19, 12, 2, 2, 'white');
    api.line(10, 21, 19, 21, 'brown');
  }),
  createTemplate('butterfly', 'Butterfly', 'flowers', 'medium', ['garden'], (api) => {
    api.ellipse(9, 11, 5, 5, 'pink');
    api.ellipse(20, 11, 5, 5, 'lavender');
    api.ellipse(9, 19, 5, 5, 'coral');
    api.ellipse(20, 19, 5, 5, 'purple');
    api.rect(13, 8, 3, 14, 'brown');
    api.set(12, 9, 'white');
    api.set(17, 9, 'white');
    api.line(14, 8, 10, 4, 'brown');
    api.line(15, 8, 19, 4, 'brown');
  }),
  createTemplate('panda', 'Panda Face', 'animals', 'medium', ['bear', 'face'], (api) => {
    api.ellipse(14, 15, 9, 9, 'white');
    api.ellipse(7, 7, 4, 4, 'black');
    api.ellipse(21, 7, 4, 4, 'black');
    api.ellipse(10, 14, 3, 3, 'black');
    api.ellipse(18, 14, 3, 3, 'black');
    api.set(10, 14, 'white');
    api.set(18, 14, 'white');
    api.ellipse(14, 18, 2, 1, 'black');
    api.line(12, 21, 16, 21, 'black');
  }),
  createTemplate('ghost', 'Little Ghost', 'characters', 'easy', ['spooky'], (api) => {
    api.ellipse(14, 13, 7, 8, 'white');
    api.rect(7, 13, 15, 10, 'white');
    api.set(10, 14, 'black');
    api.set(18, 14, 'black');
    api.ellipse(14, 17, 2, 2, 'black');
    api.polygon([[7, 23], [10, 20], [13, 23], [16, 20], [19, 23], [22, 20], [22, 24], [7, 24]], 'white');
  }),
  createTemplate('cupcake', 'Cupcake', 'keychains', 'medium', ['dessert'], (api) => {
    api.rect(9, 16, 11, 9, 'sky');
    api.line(11, 17, 11, 24, 'blue');
    api.line(15, 17, 15, 24, 'blue');
    api.line(19, 17, 19, 24, 'blue');
    api.ellipse(14, 14, 8, 5, 'pink');
    api.ellipse(14, 10, 5, 4, 'cream');
    api.set(14, 6, 'red');
    api.set(10, 13, 'yellow');
    api.set(17, 12, 'teal');
  }),
  createTemplate('gold-key', 'Gold Key', 'keychains', 'easy', ['object'], (api) => {
    api.ellipse(9, 13, 5, 5, 'gold');
    api.ellipse(9, 13, 2, 2, 'transparent');
    api.line(14, 13, 24, 13, 'gold', 3);
    api.rect(22, 14, 2, 4, 'gold');
    api.rect(18, 14, 2, 3, 'gold');
  }),
  createTemplate('rocket', 'Rocket', 'icons', 'medium', ['space'], (api) => {
    api.polygon([[14, 3], [20, 13], [18, 23], [10, 23], [8, 13]], 'white');
    api.polygon([[14, 3], [18, 10], [10, 10]], 'red');
    api.ellipse(14, 14, 3, 3, 'sky');
    api.polygon([[9, 20], [4, 25], [10, 24]], 'blue');
    api.polygon([[19, 20], [25, 25], [18, 24]], 'blue');
    api.polygon([[12, 23], [16, 23], [14, 28]], 'orange');
  }),
  createTemplate('pizza-slice', 'Pizza Slice', 'coasters', 'easy', ['food'], (api) => {
    api.polygon([[6, 6], [24, 10], [12, 25]], 'tan');
    api.polygon([[8, 9], [21, 12], [13, 22]], 'yellow');
    api.line(7, 7, 23, 11, 'brown', 2);
    api.ellipse(13, 14, 2, 2, 'red');
    api.ellipse(17, 17, 2, 2, 'red');
    api.ellipse(12, 20, 2, 2, 'red');
  }),
  createTemplate('turtle', 'Turtle', 'animals', 'medium', ['ocean'], (api) => {
    api.ellipse(14, 16, 8, 6, 'dark_green');
    api.ellipse(14, 16, 5, 4, 'green');
    api.ellipse(23, 15, 3, 3, 'green');
    api.set(24, 14, 'black');
    api.ellipse(7, 10, 3, 2, 'green');
    api.ellipse(7, 22, 3, 2, 'green');
    api.ellipse(18, 9, 3, 2, 'green');
    api.ellipse(18, 23, 3, 2, 'green');
    api.line(11, 14, 17, 18, 'mint');
    api.line(17, 14, 11, 18, 'mint');
  }),
  createTemplate('smiley', 'Smiley', 'icons', 'easy', ['emoji'], (api) => {
    api.ellipse(14, 14, 10, 10, 'yellow');
    api.set(10, 12, 'black');
    api.set(18, 12, 'black');
    api.line(9, 18, 11, 20, 'black');
    api.line(11, 20, 17, 20, 'black');
    api.line(17, 20, 19, 18, 'black');
    api.set(10, 8, 'gold');
    api.set(11, 7, 'gold');
  })
];

export const templateCategories: Array<{ id: TemplatePattern['category'] | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'animals', label: 'Animals' },
  { id: 'icons', label: 'Icons' },
  { id: 'characters', label: 'Characters' },
  { id: 'flowers', label: 'Flowers' },
  { id: 'keychains', label: 'Keychains' },
  { id: 'coasters', label: 'Coasters' }
];
